<template>
  <a-modal
    title="Edit Formula"
    :visible="visible"
    :confirmLoading="saving"
    :width="'100%'"
    :style="{ top: 0, paddingBottom: 0 }"
    :bodyStyle="{ height: 'calc(100vh - 55px)', overflow: 'auto', padding: '24px' }"
    wrapClassName="fullscreen-modal"
    @ok="handleOk"
    @cancel="handleCancel"
  >
    <div class="rule-builder">
      <div class="variable-section">
        <h3>Available Variables</h3>
        <a-checkbox-group v-model="selectedVariables" @change="onVariableChange">
          <a-checkbox
            v-for="item in collectionfeatures"
            :key="item.itemValue"
            :value="item.itemValue">
            {{ item.itemText }}
          </a-checkbox>
        </a-checkbox-group>
      </div>

      <div class="branches-section">
        <h3>Condition Branch</h3>
        <div v-for="(branch, index) in branches" :key="index" class="branch-item">
          <div class="branch-header">
            <span style="margin-right: 10px; font-weight: 500">
              <template v-if="branch.type === 'if'">if</template>
              <template v-else-if="branch.type === 'elseIf'">else if</template>
              <template v-else>else</template>
            </span>
            <a-button
              type="danger"
              icon="delete"
              size="small"
              @click="removeBranch(index)"
              v-if="branches.length > 1"
              >Delete</a-button
            >
          </div>

          <div class="conditions-group" v-if="branch.type !== 'else'">
            <!-- Condition group list -->
            <div v-for="(group, groupIndex) in (branch.conditionGroups || [])" :key="groupIndex" class="condition-group-item" style="margin-bottom: 15px; padding: 10px; border: 1px solid #e8e8e8; border-radius: 4px;">
              <div style="margin-bottom: 8px; font-weight: 500; color: #666;">
                <span>Condition Group {{ groupIndex + 1 }}</span>
                <a-button 
                  type="danger" 
                  icon="delete" 
                  size="small" 
                  @click="removeConditionGroup(index, groupIndex)"
                  :disabled="branch.conditionGroups.length === 1 || branch.type === 'else'"
                  style="float: right;"
                >
                  Delete Group
                </a-button>
              </div>
              
              <!-- Conditions in condition group (using && Connection) -->
              <div v-for="(condition, condIndex) in group.conditions" :key="condIndex" class="condition-item" style="margin-bottom: 8px;">
                <a-select
                  v-model="condition.variable"
                  style="width: 150px; margin-right: 10px"
                  :disabled="branch.type === 'else'"
                >
                  <template v-for="item in collectionfeatures">
                    <a-select-option
                      v-if="selectedVariables.includes(item.itemValue)"
                      :key="item.itemValue"
                      :value="item.itemValue">
                      {{ item.itemText }}
                    </a-select-option>
                  </template>
                </a-select>

                <a-select 
                  v-model="condition.operator" 
                  style="width: 100px; margin-right: 10px"
                  :disabled="branch.type === 'else'"
                >
                  <a-select-option value=">">Greater Than (&gt;)</a-select-option>
                  <a-select-option value="<">Less Than (&lt;)</a-select-option>
                  <a-select-option value=">=">Greater Than or Equal (&gt;=)</a-select-option>
                  <a-select-option value="<=">Less Than or Equal (&lt;=)</a-select-option>
                  <a-select-option value="==">Equal (==)</a-select-option>
                  <a-select-option value="!=">Not Equal (!=)</a-select-option>
                </a-select>

                <a-input-number 
                  v-model="condition.value" 
                  style="width: 120px; margin-right: 10px"
                  :disabled="branch.type === 'else'"
                />

                <a-button 
                  type="danger" 
                  icon="delete" 
                  size="small" 
                  @click="removeCondition(index, groupIndex, condIndex)"
                  :disabled="group.conditions.length === 1 || branch.type === 'else'"
                >
                  Delete
                </a-button>
              </div>

              <a-button 
                type="dashed" 
                icon="plus" 
                size="small" 
                @click="addCondition(index, groupIndex)"
                style="margin-top: 5px"
                :disabled="branch.type === 'else'"
              >
                Add Condition (AND &&)
              </a-button>
            </div>

            <!-- Add condition group button (between condition groups use || Connection) -->
            <a-button 
              type="dashed" 
              icon="plus" 
              size="small" 
              @click="addConditionGroup(index)"
              style="margin-top: 10px"
            >
              Add Condition Group (OR ||)
            </a-button>
          </div>
          <div v-else style="padding: 10px; color: #999; font-style: italic;">
            Return When Above Conditions Not Met false
          </div>
        </div>

        <a-button 
          type="dashed" 
          icon="plus" 
          @click="addBranch"
          style="margin-top: 20px; width: 100%"
          :disabled="branches.length > 0 && branches[branches.length - 1].type === 'else'"
        >
          Add Branch
        </a-button>
      </div>

      <div class="preview-section">
        <h3>Generated Function Code</h3>
        <a-textarea :value="generatedCode" :rows="15" readonly class="code-preview" />
        <div style="margin-top: 10px">
          <a-button type="primary" @click="copyCode">Copy Code</a-button>
          <a-button @click="formatCode" style="margin-left: 10px">Format</a-button>
        </div>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { saveOrUpdateSecond } from '@/webpublicapi/collection'

export default {
  name: 'CheckJexl',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    record: {
      type: Object,
      default: () => ({})
    },
    collectionfeatures: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      selectedVariables: [],
      branches: [],
      saving: false,
      currentEditRecord: null
    }
  },
  computed: {
    generatedCode() {
      return this.generateFunctionCode()
    }
  },
  watch: {
    visible(newVal) {
      if (newVal && this.record) {
        this.initData()
      }
    },
    record: {
      handler(newVal) {
        if (newVal && this.visible) {
          this.initData()
        }
      },
      deep: true
    }
  },
  methods: {
    initData() {
      this.currentEditRecord = this.record
      // Check if has jexl Formula
      if (this.record.jexl && this.record.jexl.trim()) {
        // If jexl Not empty, from jexl Extract used variables from
        const usedVariables = this.extractVariablesFromJexl(this.record.jexl)
        if (usedVariables.length > 0) {
          this.selectedVariables = usedVariables
        } else {
          // If extraction fails, select the first one by default
          if (this.collectionfeatures && this.collectionfeatures.length > 0) {
            this.selectedVariables = [this.collectionfeatures[0].itemValue]
          }
        }
        // Parse existing formula
        this.parseJexlToBranches(this.record.jexl, this.record.jexlResult)
      } else {
        // If jexl Empty, only select the first variable
        if (this.collectionfeatures && this.collectionfeatures.length > 0) {
          this.selectedVariables = [this.collectionfeatures[0].itemValue]
        }
        // Use default value
        this.resetBranches()
      }
    },
    handleOk() {
      // Generate entire function code as jexl
      const jexl = this.generateFunctionCode()
      const jexlResult = this.generateJexlResult()
      
      if (!this.currentEditRecord || !this.currentEditRecord.id) {
        this.$message.error('Missing record info, cannot save')
        return
      }
      
      // Save formula
      this.saving = true
      const payload = {
        ...this.currentEditRecord,
        jexl: jexl,
        jexlResult: jexlResult
      }
      
      saveOrUpdateSecond(payload)
        .then((res) => {
          if (res.code === 1000) {
            this.$message.success('Formula saved successfully')
            this.$emit('ok', payload)
            this.$emit('update:visible', false)
          } else {
            this.$message.error(res.msg || 'Save failed')
          }
          this.saving = false
        })
        .catch((e) => {
          this.saving = false
          this.$message.error(e.message || 'Save failed')
        })
    },
    handleCancel() {
      this.$emit('cancel')
      this.$emit('update:visible', false)
    },
    onVariableChange() {
      this.$forceUpdate()
    },
    addBranch() {
      const newType = this.branches.length === 1 ? 'elseIf' : 'else'
      if (this.branches.length > 0 && this.branches[this.branches.length - 1].type === 'else') {
        this.branches[this.branches.length - 1].type = 'elseIf'
      }
      
      if (newType === 'else') {
        this.branches.push({
          type: 'else',
          conditionGroups: []
        })
      } else {
        const defaultVariable = this.collectionfeatures && this.collectionfeatures.length > 0 
          ? this.collectionfeatures[0].itemValue 
          : (this.selectedVariables[0] || 'period')
        this.branches.push({
          type: newType,
          conditionGroups: [
            {
              conditions: [
                {
                  variable: defaultVariable,
                  operator: '==',
                  value: 1,
                },
              ],
            },
          ],
        })
      }
    },
    removeBranch(index) {
      this.branches.splice(index, 1)
      this.updateBranchTypes()
    },
    updateBranchTypes() {
      this.branches.forEach((branch, index) => {
        if (index === 0) {
          branch.type = 'if'
        } else if (index === this.branches.length - 1) {
          branch.type = 'else'
        } else {
          branch.type = 'elseIf'
        }
      })
    },
    addConditionGroup(branchIndex) {
      const branch = this.branches[branchIndex]
      const defaultVariable = this.collectionfeatures && this.collectionfeatures.length > 0 
        ? this.collectionfeatures[0].itemValue 
        : (this.selectedVariables[0] || 'period')
      branch.conditionGroups.push({
        conditions: [
          {
            variable: defaultVariable,
            operator: '==',
            value: 1,
          },
        ],
      })
    },
    removeConditionGroup(branchIndex, groupIndex) {
      const branch = this.branches[branchIndex]
      branch.conditionGroups.splice(groupIndex, 1)
    },
    addCondition(branchIndex, groupIndex) {
      const branch = this.branches[branchIndex]
      const group = branch.conditionGroups[groupIndex]
      const defaultVariable = this.collectionfeatures && this.collectionfeatures.length > 0 
        ? this.collectionfeatures[0].itemValue 
        : (this.selectedVariables[0] || 'period')
      group.conditions.push({
        variable: defaultVariable,
        operator: '==',
        value: 1,
      })
    },
    removeCondition(branchIndex, groupIndex, conditionIndex) {
      const branch = this.branches[branchIndex]
      const group = branch.conditionGroups[groupIndex]
      group.conditions.splice(conditionIndex, 1)
    },
    extractVariablesFromJexl(jexl) {
      const variables = []
      if (!jexl || typeof jexl !== 'string') {
        return variables
      }
      
      const availableVars = this.collectionfeatures ? this.collectionfeatures.map(item => item.itemValue) : []
      if (availableVars.length === 0) {
        return variables
      }
      
      if (jexl.includes('var ') || jexl.includes('function')) {
        const varMatches = jexl.matchAll(/var\s+(\w+)\s*=/g)
        for (const match of varMatches) {
          const varName = match[1]
          if (varName && availableVars.includes(varName) && !variables.includes(varName)) {
            variables.push(varName)
          }
        }
      }
      
      if (variables.length === 0 || !jexl.includes('var ')) {
        availableVars.forEach(varName => {
          const regex = new RegExp(`\\b${varName}\\b\\s*([><=!]|\\s)`, 'g')
          if (regex.test(jexl)) {
            if (!variables.includes(varName)) {
              variables.push(varName)
            }
          }
        })
      }
      
      return variables
    },
    parseJexlToBranches(jexl, jexlResult) {
      try {
        this.branches = []
        
        if (typeof jexl === 'string' && jexl.trim()) {
          let jexlStr = jexl.trim()
          
          // Extract all if/else if Branch
          if (jexlStr.includes('function') || jexlStr.includes('if(') || jexlStr.includes('if (')) {
            // Match all if and else if statements, correctly handle parenthesis nesting
            const branches = []
            let searchIndex = 0
            
            while (searchIndex < jexlStr.length) {
              // Find if or else if
              const ifMatch = jexlStr.substring(searchIndex).match(/(?:else\s+)?if\s*\(/)
              if (!ifMatch) {
                break
              }
              
              const ifStart = searchIndex + ifMatch.index + ifMatch[0].length
              const isElseIf = ifMatch[0].startsWith('else')
              
              // Start from if( then find matching right parenthesis
              let depth = 1
              let conditionEnd = ifStart
              
              for (let i = ifStart; i < jexlStr.length && depth > 0; i++) {
                if (jexlStr[i] === '(') {
                  depth++
                } else if (jexlStr[i] === ')') {
                  depth--
                  if (depth === 0) {
                    conditionEnd = i
                    break
                  }
                }
              }
              
              if (depth === 0) {
                const conditionStr = jexlStr.substring(ifStart, conditionEnd).trim()
                branches.push({
                  type: branches.length === 0 ? 'if' : (isElseIf ? 'elseIf' : 'if'),
                  conditionStr: conditionStr
                })
                searchIndex = conditionEnd + 1
              } else {
                // If no matching right parenthesis found, skip this if
                searchIndex = ifStart
                break
              }
            }
            
            // Check if has else Branch
            if (jexlStr.includes('else {') || jexlStr.includes('else{')) {
              branches.push({
                type: 'else',
                conditionStr: null
              })
            }
            
            // Parse each branch
            branches.forEach((branch, index) => {
              if (branch.type === 'else') {
                this.branches.push({
                  type: 'else',
                  conditionGroups: []
                })
              } else if (branch.conditionStr) {
                // Parse condition string
                const conditionGroups = this.parseConditionGroupsFromString(branch.conditionStr)
                if (conditionGroups.length > 0) {
                  this.branches.push({
                    type: branch.type,
                    conditionGroups: conditionGroups
                  })
                }
              }
            })
          } else {
            // If not in function format, try parsing the condition expression directly
            const conditionGroups = this.parseConditionGroupsFromString(jexlStr)
            if (conditionGroups.length > 0) {
              this.branches.push({
                type: 'if',
                conditionGroups: conditionGroups
              })
            }
          }
        }
        
        if (this.branches.length === 0) {
          this.resetBranches()
        } else {
          this.branches.forEach(branch => {
            if (!branch.conditionGroups) {
              branch.conditionGroups = []
            }
          })
        }
      } catch (e) {
        console.error('Parse jexl Failed:', e)
        this.resetBranches()
      }
    },
    parseConditionGroupsFromString(conditionStr) {
      // First by || Split condition groups (considering parenthesis nesting)
      const orParts = this.splitByOrOperator(conditionStr)
      
      if (orParts.length === 0) {
        orParts.push(conditionStr)
      }
      
      return this.parseConditionGroups(orParts)
    },
    splitByOrOperator(jexlStr) {
      const orParts = []
      let currentPart = ''
      let depth = 0
      let inString = false
      let stringChar = ''
      
      for (let i = 0; i < jexlStr.length; i++) {
        const char = jexlStr[i]
        const nextChar = jexlStr[i + 1]
        
        // Process string literals
        if ((char === '"' || char === "'") && (i === 0 || jexlStr[i - 1] !== '\\')) {
          if (!inString) {
            inString = true
            stringChar = char
          } else if (char === stringChar) {
            inString = false
            stringChar = ''
          }
          currentPart += char
          continue
        }
        
        if (inString) {
          currentPart += char
          continue
        }
        
        // Handle parentheses
        if (char === '(') {
          depth++
          currentPart += char
        } else if (char === ')') {
          depth--
          currentPart += char
        } else if (char === '|' && nextChar === '|' && depth === 0) {
          // At top level encounter ||, split condition groups
          const trimmed = currentPart.trim()
          if (trimmed) {
            orParts.push(trimmed)
          }
          currentPart = ''
          i++ // Skip next |
        } else {
          currentPart += char
        }
      }
      
      const trimmed = currentPart.trim()
      if (trimmed) {
        orParts.push(trimmed)
      }
      
      return orParts
    },
    parseConditionGroups(orParts) {
      const conditionGroups = []
      
      orParts.forEach((orPart) => {
        let cleanPart = orPart.trim()
        
        // Remove outer parentheses (if the entire condition group is wrapped)
        cleanPart = this.removeOuterParentheses(cleanPart)
        
        // Split by && (considering parenthesis nesting)
        const andParts = this.splitByAndOperator(cleanPart)
        
        if (andParts.length === 0 && cleanPart.trim()) {
          andParts.push(cleanPart.trim())
        }
        
        const conditions = []
        
        andParts.forEach((andPart) => {
          const trimmed = andPart.trim()
          // Remove outer parentheses of the condition itself
          const cleaned = this.removeOuterParentheses(trimmed)
          const condition = this.parseSingleCondition(cleaned)
          if (condition) {
            conditions.push(condition)
          }
        })
        
        if (conditions.length > 0) {
          conditionGroups.push({ conditions })
        } else {
          // Try parsing the entire condition group directly
          const condition = this.parseSingleCondition(cleanPart)
          if (condition) {
            conditionGroups.push({
              conditions: [condition]
            })
          } else {
            console.warn('ConditionGroupbe emptyAndcannot be parsedIsSingleCondition,Skipped:', orPart)
          }
        }
      })
      
      return conditionGroups
    },
    removeOuterParentheses(str) {
      let cleanStr = str.trim()
      if (cleanStr.startsWith('(') && cleanStr.endsWith(')')) {
        let depth = 0
        let isComplete = true
        for (let i = 0; i < cleanStr.length; i++) {
          if (cleanStr[i] === '(') depth++
          if (cleanStr[i] === ')') depth--
          // If depth changes at middle position to0, indicating not a complete outer parenthesis
          if (i > 0 && i < cleanStr.length - 1 && depth === 0) {
            isComplete = false
            break
          }
        }
        if (isComplete && depth === 0) {
          cleanStr = cleanStr.slice(1, -1).trim()
        }
      }
      return cleanStr
    },
    splitByAndOperator(conditionStr) {
      const andParts = []
      let currentPart = ''
      let depth = 0
      let inString = false
      let stringChar = ''
      
      for (let i = 0; i < conditionStr.length; i++) {
        const char = conditionStr[i]
        const nextChar = conditionStr[i + 1]
        
        // Process string literals
        if ((char === '"' || char === "'") && (i === 0 || conditionStr[i - 1] !== '\\')) {
          if (!inString) {
            inString = true
            stringChar = char
          } else if (char === stringChar) {
            inString = false
            stringChar = ''
          }
          currentPart += char
          continue
        }
        
        if (inString) {
          currentPart += char
          continue
        }
        
        // Handle parentheses
        if (char === '(') {
          depth++
          currentPart += char
        } else if (char === ')') {
          depth--
          currentPart += char
        } else if (char === '&' && nextChar === '&' && depth === 0) {
          // At top level encounter &&, split conditions
          const trimmed = currentPart.trim()
          if (trimmed) {
            andParts.push(trimmed)
          }
          currentPart = ''
          i++ // Skip next &
        } else {
          currentPart += char
        }
      }
      
      const trimmed = currentPart.trim()
      if (trimmed) {
        andParts.push(trimmed)
      }
      
      return andParts
    },
    parseSingleCondition(conditionStr) {
      const trimmed = conditionStr.trim()
      // Remove outer parentheses
      let cleanCondition = this.removeOuterParentheses(trimmed)
      
      // Match variable names, operators and values
      // Support: variable operator value Format
      // Operator: >=, <=, ==, !=, >, <, =
      // Note: match >= and <= first, then > and <, to avoid false matching
      const match = cleanCondition.match(/^(\w+)\s*(>=|<=|==|!=|>|<|=)\s*(.+)$/)
      if (match) {
        let operator = match[2].trim()
        let value = match[3].trim()
        
        // Single will = Convert to ==
        if (operator === '=') operator = '=='
        
        // Process value (could be number, string or variable)
        let numValue
        // Remove string quotes
        if ((value.startsWith('"') && value.endsWith('"')) || 
            (value.startsWith("'") && value.endsWith("'"))) {
          value = value.slice(1, -1).trim()
          numValue = value
        } else {
          // Try parsing as number
          numValue = parseInt(value)
          if (isNaN(numValue)) {
            numValue = parseFloat(value)
            if (isNaN(numValue)) {
              // If neither number nor string, keep original value
              numValue = value
            }
          }
        }
        
        return {
          variable: match[1].trim(),
          operator: operator,
          value: numValue
        }
      }
      
      return null
    },
    resetBranches() {
      const defaultVariable = this.collectionfeatures && this.collectionfeatures.length > 0 
        ? this.collectionfeatures[0].itemValue 
        : 'period'
      this.branches = [
        {
          type: 'if',
          conditionGroups: [
            {
              conditions: [
                {
                  variable: defaultVariable,
                  operator: '==',
                  value: 1,
                },
              ],
            },
          ],
        },
      ]
    },
    generateFunctionCode() {
      let code = 'function getLevel(values) {\n'
      code += '  var json = JSON.parse(values);\n'
      this.selectedVariables.forEach((variable) => {
        code += `  var ${variable} = json.${variable};\n`
      })
      code += '\n'
      this.branches.forEach((branch) => {
        if (branch.type === 'if') {
          const condition = this.generateConditionFromGroups(branch.conditionGroups)
          code += `  if(${condition}) {\n`
          code += `    return 'true';\n`
          code += '  }'
        } else if (branch.type === 'elseIf') {
          const condition = this.generateConditionFromGroups(branch.conditionGroups)
          code += ` else if(${condition}) {\n`
          code += `    return 'true';\n`
          code += '  }'
        } else if (branch.type === 'else') {
          code += ' else {\n'
          code += `    return 'false';\n`
          code += '  }'
        }
      })
      code += '\n}'
      return code
    },
    generateConditionFromGroups(conditionGroups) {
      if (!conditionGroups || conditionGroups.length === 0) {
        return 'true'
      }
      return conditionGroups.map((group) => {
        if (!group.conditions || group.conditions.length === 0) {
          return ''
        }
        const groupConditions = group.conditions.map((cond) => {
          const operator = cond.operator === '==' ? '==' : cond.operator
          return `${cond.variable} ${operator} ${cond.value}`
        }).join(' && ')
        return group.conditions.length > 1 ? `(${groupConditions})` : groupConditions
      }).filter(g => g).join(' || ')
    },
    generateJexlResult() {
      if (!this.branches || this.branches.length === 0) {
        return 'false'
      }
      
      const hasConditionBranches = this.branches.some(
        (branch) => (branch.type === 'if' || branch.type === 'elseIf') && branch.conditionGroups && branch.conditionGroups.length > 0 && branch.conditionGroups.some(g => g.conditions && g.conditions.length > 0)
      )
      
      return hasConditionBranches ? 'true' : 'false'
    },
    copyCode() {
      const textarea = document.createElement('textarea')
      textarea.value = this.generatedCode
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      this.$message.success('Code copied to clipboard')
    },
    formatCode() {
      this.$forceUpdate()
      this.$message.success('Code formatted')
    }
  }
}
</script>

<style lang="less" scoped>
.rule-builder {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.variable-section {
  background: #fff;
  padding: 16px;
  border-radius: 4px;
  border: 1px solid #e8e8e8;

  h3 {
    margin-bottom: 16px;
    font-size: 16px;
    font-weight: 500;
  }
}

.branches-section {
  background: #fff;
  padding: 16px;
  border-radius: 4px;
  border: 1px solid #e8e8e8;

  h3 {
    margin-bottom: 16px;
    font-size: 16px;
    font-weight: 500;
  }
}

.branch-item {
  margin-bottom: 24px;
  padding: 16px;
  background: #fafafa;
  border-radius: 4px;
  border: 1px solid #e8e8e8;

  &:last-child {
    margin-bottom: 0;
  }
}

.branch-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e8e8e8;
}

.conditions-group {
  margin-bottom: 16px;
}

.condition-item {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  flex-wrap: wrap;
  gap: 8px;
}

.preview-section {
  background: #fff;
  padding: 16px;
  border-radius: 4px;
  border: 1px solid #e8e8e8;

  h3 {
    margin-bottom: 16px;
    font-size: 16px;
    font-weight: 500;
  }
}

.code-preview {
  font-family: 'Courier New', monospace;
  font-size: 14px;
  background: #f5f5f5;
  border: 1px solid #d9d9d9;
}

/* Fullscreen modal Style */
/deep/ .fullscreen-modal {
  .ant-modal {
    max-width: 100% !important;
    top: 0 !important;
    padding-bottom: 0 !important;
    margin: 0 !important;
  }
  .ant-modal-content {
    width: 100vw !important;
    height: 100vh !important;
    display: flex;
    flex-direction: column;
  }
  .ant-modal-body {
    flex: 1;
    overflow: auto;
  }
}
</style>

